/**
 * <product-faq> — PDP FAQ accordions.
 *
 * The behaviour half of Intelligems test 01, which won on the ritual PDP. The whole
 * injection scaffold is gone — no mounting, no MutationObserver, no failsafe, no hiding
 * the theme's own accordions — because the markup is now server-rendered. What is left
 * is the three things the test actually earned:
 *
 *   1. Animated open/close. <details> cannot transition its own height, so the reveal
 *      wrapper is animated and the element's `open` is set around it. Reopening
 *      mid-collapse resumes from the current height instead of snapping to 0.
 *   2. Anchor-on-open. Opening a question below an open, taller one drags it up by that
 *      height as the other collapses. The resting position is predicted before the
 *      scroll, not measured after it — that is what used to strand people below the FAQ.
 *   3. A settling scroll for in-answer jump links. Lazy media between the FAQ and the
 *      target keeps resizing while the smooth scroll runs, so the landing is re-aimed
 *      until it stops moving rather than trusted from the distance measured on click.
 *
 * Jump links are plain `<a href="#some-id">` written by the merchant in the metaobject.
 * The test carried a hardcoded map of live section ids; nothing here needs configuring.
 *
 * With JS off, or before this upgrades, the native <details> still opens and closes.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('product-faq')) return;

  var MOBILE_BREAKPOINT = 749;
  var HEADER_FALLBACK = { desktop: 76, mobile: 69 };
  var SCROLL_GAP = 8;
  var ANCHOR_TAIL_ROOM = 80;
  var ANIM = { min: 200, max: 420, perPx: 0.35, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' };
  var SETTLE = { tries: 12, ms: 120, tolerance: 4 };

  function reducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function viewportHeight() {
    return window.innerHeight || document.documentElement.clientHeight;
  }

  /** The theme leaves --header-height at a bogus value, so measure and sanity-check. */
  function headerOffset() {
    var header = document.querySelector('.section-header');
    var height = header ? header.getBoundingClientRect().height : 0;
    if (!height || height > 200) {
      height = window.innerWidth > MOBILE_BREAKPOINT ? HEADER_FALLBACK.desktop : HEADER_FALLBACK.mobile;
    }
    return height + SCROLL_GAP;
  }

  function duration(px) {
    return Math.min(ANIM.max, Math.max(ANIM.min, px * ANIM.perPx));
  }

  class ProductFaq extends HTMLElement {
    connectedCallback() {
      if (this.dataset.pfaqReady === 'true') return;
      this.dataset.pfaqReady = 'true';

      this.addEventListener('click', this.onClick.bind(this));
      this.items().forEach(
        function (item) {
          item.addEventListener('toggle', this.onToggle.bind(this));
        }.bind(this)
      );
    }

    items() {
      return Array.prototype.slice.call(this.querySelectorAll('details.pfaq__item'));
    }

    revealOf(item) {
      return item.querySelector('.pfaq__reveal');
    }

    onClick(event) {
      var jump = event.target.closest ? event.target.closest('.pfaq__rte a[href^="#"]') : null;
      if (jump) return this.onJump(event, jump);

      var summary = event.target.closest ? event.target.closest('summary.pfaq__summary') : null;
      if (!summary) return;

      // Drive open/close here so a collapse can animate before <details> hides its content.
      event.preventDefault();
      var item = summary.parentNode;
      if (item.open && !item.pfaqClosing) {
        this.close(item);
      } else {
        this.anchorTo(item);
        this.open(item);
      }
    }

    /** One question open at a time. */
    onToggle(event) {
      var opened = event.target;
      if (!opened.open) return;
      this.items().forEach(
        function (item) {
          if (item !== opened) this.close(item);
        }.bind(this)
      );
    }

    // ----------------------------------------------------------------- animation

    // cancel() reverts the effect and queues oncancel, so callers read the height first.
    stopAnim(item) {
      if (item.pfaqAnim) item.pfaqAnim.cancel();
    }

    slide(item, from, to, onDone, onAbort) {
      var box = this.revealOf(item);
      if (reducedMotion() || !box || !box.animate) {
        if (onDone) onDone();
        return;
      }

      box.classList.add('pfaq__reveal--animating');
      var run = box.animate([{ height: from + 'px' }, { height: to + 'px' }], {
        duration: duration(Math.abs(to - from)),
        easing: ANIM.easing,
      });
      item.pfaqAnim = run;

      // Only the animation that still owns the element cleans up, so a queued cancel
      // cannot strip a newer run.
      var release = function () {
        if (item.pfaqAnim !== run) return;
        item.pfaqAnim = null;
        box.classList.remove('pfaq__reveal--animating');
      };
      run.onfinish = function () {
        release();
        if (onDone) onDone();
      };
      run.oncancel = function () {
        release();
        if (onAbort) onAbort();
      };
    }

    open(item) {
      var box = this.revealOf(item);
      // Reopening mid-collapse resumes from wherever the content is, not from 0.
      var from = item.open && box ? box.getBoundingClientRect().height : 0;
      this.stopAnim(item);
      item.pfaqClosing = false;
      item.open = true;
      if (box) this.slide(item, from, box.scrollHeight);
    }

    close(item) {
      if (!item.open || item.pfaqClosing) return;
      var box = this.revealOf(item);
      var from = box ? box.getBoundingClientRect().height : 0;
      this.stopAnim(item);
      item.pfaqClosing = true;
      this.slide(
        item,
        from,
        0,
        function () {
          item.pfaqClosing = false;
          item.open = false;
        },
        function () {
          item.pfaqClosing = false;
        }
      );
    }

    // ------------------------------------------------------------------ scrolling

    /** Height an open item contributes on top of its summary row. */
    expandedBy(item) {
      var box = this.revealOf(item);
      var height = box ? box.getBoundingClientRect().height : 0;
      return height + (parseFloat(window.getComputedStyle(item).marginBottom) || 0);
    }

    /** Height about to disappear above `item` when the currently open question collapses. */
    shrinkAbove(item) {
      var total = 0;
      var all = this.items();
      for (var i = 0; i < all.length; i++) {
        if (all[i] === item) break;
        if (all[i].open) total += this.expandedBy(all[i]);
      }
      return total;
    }

    /**
     * Call before opening. The collapse of a taller question above drags this one up by
     * that height, so the resting position has to be predicted rather than measured.
     */
    anchorTo(item) {
      var summary = item.querySelector('summary.pfaq__summary');
      if (!summary) return;
      var finalTop = summary.getBoundingClientRect().top - this.shrinkAbove(item);
      var offset = headerOffset();
      if (finalTop >= offset && finalTop <= viewportHeight() - ANCHOR_TAIL_ROOM) return;
      window.scrollTo({ top: Math.max(0, finalTop + window.pageYOffset - offset), behavior: 'smooth' });
    }

    /**
     * `#foo` matches an exact id first, then any id ENDING in `foo`.
     *
     * The suffix pass exists because Shopify prefixes section ids with the template's
     * numeric id — `#shopify-section-template--29761002373462__multicolumn_FCtQxB`. That
     * number changes if the template is ever recreated, which would quietly break every
     * link written into a metafield. Linking to `#multicolumn_FCtQxB` survives it.
     */
    resolveTarget(id) {
      var exact = document.getElementById(id);
      if (exact) return exact;
      try {
        return document.querySelector('[id$="' + CSS.escape(id) + '"]');
      } catch (error) {
        return null;
      }
    }

    onJump(event, anchor) {
      var id = String(anchor.getAttribute('href') || '').slice(1);
      if (!id) return;
      var target = this.resolveTarget(id);
      if (!target) return;

      event.preventDefault();
      var host = anchor.closest('details.pfaq__item');
      // The FAQ sits above every target, so discount the height the closing question is
      // about to give back.
      var shrink = host ? this.expandedBy(host) : 0;
      if (host) this.close(host);
      this.scrollToTarget(target, shrink);
    }

    scrollToTarget(target, shrink) {
      var offset = headerOffset();
      var tries = 0;
      var timer = null;

      var aim = function (discount) {
        var top = target.getBoundingClientRect().top + window.pageYOffset - offset - discount;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      };

      var stop = function () {
        if (timer) window.clearInterval(timer);
        timer = null;
        window.removeEventListener('wheel', stop);
        window.removeEventListener('touchstart', stop);
        window.removeEventListener('keydown', stop);
      };

      // Hand control back the moment the visitor scrolls themselves.
      window.addEventListener('wheel', stop, { passive: true });
      window.addEventListener('touchstart', stop, { passive: true });
      window.addEventListener('keydown', stop);

      // The first aim discounts the question that is collapsing; later ones read the
      // live position, because lazy media between here and the target keeps resizing.
      aim(shrink);
      timer = window.setInterval(function () {
        tries++;
        var delta = target.getBoundingClientRect().top - offset;
        if (Math.abs(delta) <= SETTLE.tolerance || tries >= SETTLE.tries) {
          stop();
          return;
        }
        aim(0);
      }, SETTLE.ms);
    }
  }

  customElements.define('product-faq', ProductFaq);
})();
